import { lazy, Suspense, useEffect } from "react";
import { MotionConfig } from "framer-motion";
import { useUIStore } from "@/stores/uiStore";
import { useReplayTimer } from "@/hooks/useReplayTimer";
import { useKeyboardNavigation } from "@/hooks/useKeyboardNavigation";
import { useKeyboardShortcuts } from "@/hooks/use-keyboard-shortcuts";
import { useReplayKeyboard } from "@/hooks/useReplayKeyboard";
import { useReplayResumeSeek } from "@/hooks/useReplayResumeSeek";
import { useReplayBackfill } from "@/hooks/useReplayBackfill";
import { useReplayContextRefresh } from "@/hooks/useReplayContextRefresh";
import { useCsvRestore } from "@/hooks/useCsvRestore";

import { WorkspaceShell } from "@/components/layout/WorkspaceShell";
import { ToastContainer } from "@/components/lytick/ToastContainer";
import { PwaClient } from "@/components/lytick/PwaClient";
import { BootScreen } from "@/components/lytick/BootScreen";
import { ReplayLoadingOverlay } from "@/components/lytick/ReplayLoadingOverlay";
import { CsvImportPreviewDialog } from "@/components/features/CsvImportPreviewDialog";

const KeyboardShortcutsOverlay = lazy(() =>
  import("@/components/features/KeyboardShortcutsOverlay").then((m) => ({
    default: m.KeyboardShortcutsOverlay,
  })),
);

export default function App() {
  const setOrientation = useUIStore((state) => state.setOrientation);
  const pendingCsvImport = useUIStore((state) => state.pendingCsvImport);
  const clearPendingCsvImport = useUIStore((state) => state.clearPendingCsvImport);

  useCsvRestore();
  useReplayTimer();
  useKeyboardShortcuts();
  useKeyboardNavigation();
  useReplayKeyboard();
  useReplayResumeSeek();
  useReplayBackfill();
  useReplayContextRefresh();

  useEffect(() => {
    document.documentElement.classList.add("dark");
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", "#080D17");
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(orientation: landscape)");
    const handleChange = (e: MediaQueryListEvent) => {
      setOrientation(e.matches ? "landscape" : "portrait");
    };
    setOrientation(mediaQuery.matches ? "landscape" : "portrait");
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [setOrientation]);

  return (
    <MotionConfig reducedMotion="user">
      <>
        <div className="flex flex-col h-[100dvh] bg-background overflow-hidden desktop-shell">
          <WorkspaceShell />
          <Suspense fallback={null}>
            <KeyboardShortcutsOverlay />
          </Suspense>
          <ToastContainer />
          <PwaClient />
        </div>
        <BootScreen />
        <ReplayLoadingOverlay />
        {pendingCsvImport && (
          <CsvImportPreviewDialog
            file={pendingCsvImport.file}
            dataset={pendingCsvImport.dataset}
            onClose={clearPendingCsvImport}
          />
        )}
      </>
    </MotionConfig>
  );
}
